import type { components } from '../../../api/generated/schema'

/**
 * The provider-neutral shapes the link and reauth funnels are built on.
 *
 * **Settled by the plan (`2026-09-11-m5-accounts.md`, Ticket 3) and the M5
 * intake (#6) — not to be redesigned here.**
 *
 * `ProviderType` is read straight off the generated schema rather than
 * restated as a literal union — the only two spellings of the provider name
 * that may exist anywhere are the backend's own (append-only per that type's
 * own docblock) and `registry.ts`'s map keys, and typing them a second time
 * here would be a third place for the two to drift apart. It also keeps this
 * file itself clean of the string literals the guard test in
 * `registry.test.ts` polices — the exemption is `registry.ts` plus the
 * generated schema, and importing rather than restating means this file
 * needs no exemption of its own.
 */
export type ProviderType = components['schemas']['ProviderType']

/**
 * One link or reauth session, already stripped of the wire's field names.
 *
 * Mirrors `LinkSessionResponse` — `callbackHost`/`callbackPaths`/`stateNonce`
 * are `null` for Plaid (Plaid Link owns its own callback) and populated for
 * Flinks. `linkToken` is the same field carrying two different meanings by
 * provider — a Plaid Link token, or the Flinks Connect URL — disambiguated by
 * `provider`, never by sniffing the string.
 */
export type LinkSession = {
  provider: ProviderType
  linkToken: string
  callbackHost: string | null
  callbackPaths: string[] | null
  stateNonce: string | null
}

/** Where a reconnect was started from — the closed set the analytics guard admits for it. */
export const RECONNECT_ENTRY_POINTS = ['connections', 'accounts_panel', 'link_prompt'] as const
export type ReconnectEntryPoint = (typeof RECONNECT_ENTRY_POINTS)[number]

/** Which funnel a session being prepared belongs to — see `redirectFlow.prepare`.
 *  A relink also carries the two facts its analytics outcome needs after an OAuth
 *  handoff wipes memory: where it started, and when `start()` was called. */
export type PrepareContext =
  | { kind: 'link' }
  | { kind: 'reauth'; itemId: string }
  | { kind: 'relink'; relinkItemId: string; entryPoint?: ReconnectEntryPoint; startedAt?: number }

/** What an adapter hands back once its provider's flow completes. */
export type CompletionPayload = { publicToken: string; metadata: Record<string, unknown> }

/**
 * One provider's SDK/flow, behind one shape.
 *
 * `reauthRoundTripsToken` is the one place the generic layer cannot be
 * generic — see `useAggregator.ts`'s reauth flow, which reads this flag
 * instead of ever comparing a provider name. Plaid: `false` — Link's update
 * mode needs no server call, and `POST /link-session/complete` there answers
 * 409 `already_linked`. Flinks: `true` — the CSRF nonce round trip through
 * `/link-session/complete` is unchanged on a reauth.
 */
export type LinkAdapter = {
  open(session: LinkSession): Promise<CompletionPayload>
  reauthRoundTripsToken: boolean
  /**
   * Optional — #414, one grouped capability rather than three independent
   * optional members (architect review, B2: three of the five pieces the
   * first draft scattered across this interface, a free export and a direct
   * import were all one idea). Only a provider whose flow can navigate the
   * browser FULLY away (Plaid's OAuth institutions) implements this at all;
   * Flinks has none, because its own OAuth-shaped flow (`flinks.ts`'s own
   * header) opens a separate tab and never destroys this one's JS context.
   *
   * A per-adapter capability, the same shape `reauthRoundTripsToken` already
   * uses, rather than `useAggregator.ts` comparing `provider === 'plaid'`
   * itself or importing a concrete adapter module directly —
   * `registry.test.ts`'s "no provider name string literal appears outside
   * registry.ts" (#397) is a real architectural guard, and `registry.ts`'s
   * own "no call site outside this file ever compares a provider name" is
   * the reason `resume` is reached through `getAdapter`, never a static
   * import of `./plaid`.
   */
  redirectFlow?: {
    /**
     * `null`/`undefined` sends no `redirect_uri` field at all, which is the
     * normal case for every provider but Plaid, and for Plaid itself in
     * every environment but the one Plaid's dashboard has a redirect URI
     * registered for.
     */
    redirectUri(): string | null
    /**
     * Called synchronously, immediately before `open()` — so whatever the
     * provider needs to resume after Plaid's own navigation destroys every
     * in-memory value is persisted before the point of no return, which is
     * decided later, inside the provider's own SDK, and cannot be predicted
     * from here.
     *
     * `context` says which funnel this session belongs to: a fresh link, a
     * reauth of `itemId`, or a RECONNECT of the disconnected account's
     * `relinkItemId`. `LinkSession` itself carries none of that — it mirrors
     * the wire response (`#397`'s settled shape) — so this is how it reaches
     * an adapter without widening it.
     *
     * `redirectUri` arrives ALREADY RESOLVED, computed by the caller one
     * line earlier from this same object's `redirectUri()` — not
     * recomputed here — so an implementation can no-op when it is `null`
     * (security review, #414, SEC-1: with nothing sent to the backend,
     * Plaid has no URL to navigate back to, so persisting anything here
     * would be unreadable by construction).
     */
    prepare(session: LinkSession, context: PrepareContext, redirectUri: string | null): void
    /**
     * `/plaid`'s own resume, reached through `getAdapter(pending.provider)
     * ?.redirectFlow?.resume(...)` — never a direct import of a concrete
     * adapter module. Re-launches the provider's own flow with the SAME
     * token an earlier `open()` minted, plus the URL the browser actually
     * landed back on.
     */
    resume(linkToken: string, receivedRedirectUri: string): Promise<CompletionPayload>
  }
}
